import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { ToastService } from '@teorema/patron';
import { Observable } from 'rxjs';
import { navItemsV2 } from 'src/app/_nav-v2';
import { PatronNavData } from 'src/app/models';
import { LicenseSessionLockService } from 'src/app/services/license-session-lock.service';
import { utils } from 'src/app/shared';
import { AuthService } from './auth.service';

/** Header de resposta com o estado do assento desta sessão (chamado 0000577675). */
const LICENSE_SESSION_STATUS_HEADER = 'license-session-status';

/**
 * Itens do menu que declaram módulo, do caminho mais longo para o mais curto — a busca por prefixo
 * em `moduleFromMenu` fica com o primeiro que casar. Montado uma vez, na primeira requisição.
 */
let menuModules: { url: string; modules: string[] }[] | null = null;

function buildMenuModules(items: PatronNavData[], acc: { url: string; modules: string[] }[] = []) {
  for (const item of items ?? []) {
    const url = typeof item.url === 'string' ? item.url.split(/[?#]/)[0] : null;
    const modules = item.requiredModules;

    if (url?.startsWith('/') && Array.isArray(modules) && modules.length) {
      acc.push({ url: url.replace(/\/+$/, ''), modules });
    }
    if (item.children?.length) buildMenuModules(item.children, acc);
  }
  return acc;
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private toastService: ToastService,
    private router: Router,
    private licenseSessionLock: LicenseSessionLockService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.authService.useAuth) {
      return next.handle(req);
    }

    if (req.headers.has('Skip-Interceptor')) {
      const modifiedReq = req.clone({
        headers: req.headers.delete('Skip-Interceptor'),
      });
      return next.handle(modifiedReq);
    }

    let token = utils.getToken();

    let companyGroup: any = utils.getCompanyGroup();
    let user = utils.getUser();
    let customer = utils.getCustomer();
    let { system, appVersion, env, firebaseProjectId } = this.authService.getCoreConfig();
    let accessType = utils.getAccessType();
    let headers = req.headers || new HttpHeaders();
    if (system) {
      headers = headers.set('system-name', system);
    }
    if (appVersion) {
      headers = headers.set('system-version', appVersion);
    }
    if (env) {
      headers = headers.set('system-env', env);
    }
    if (firebaseProjectId) {
      headers = headers.set('firebase-project-id', firebaseProjectId);
    }

    let { href, host } = location;
    if (href) {
      headers = headers.set('system-location-href', href);
    }
    if (host) {
      headers = headers.set('system-location-host', host);
    }

    if (accessType) {
      headers = headers.set('access-type', accessType);
    }

    if (!headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/json');
    }

    if (headers.get('Content-Type') == 'none') {
      headers = headers.delete('Content-Type');
    }

    if (!headers.has('Accept')) {
      headers = headers.set('Accept', 'application/json');
    }

    if (headers.get('Accept') == 'none') {
      headers = headers.delete('Accept');
    }

    if (companyGroup?.id) {
      if (headers.has('CompanyGroupId')) {
        if (headers.get('CompanyGroupId') == 'none') {
          headers = headers.delete('CompanyGroupId');
        }
      } else {
        headers = headers.set('CompanyGroupId', companyGroup?.id);
      }
    }

    if (customer?.person_id) {
      headers = headers.set('CustomerId', customer.person_id);
    }

    // Chamado #0000577675 - so para a nossa API: header customizado em host de terceiro (cep/cnpj
    // em api.teorema.inf.br, por exemplo) dispara preflight e quebra a chamada se o CORS de la nao
    // liberar o nome. Mesmo cuidado que o `UserId` ja tem com o reverse.
    const { apiUrl } = this.authService.getCoreConfig();
    const isOwnApi = !/^https?:\/\//i.test(req.url) || (!!apiUrl && req.url.startsWith(apiUrl));

    if (isOwnApi) {
      // Identifica a MAQUINA, nao a aba. Vai em toda requisicao porque e o heartbeat
      // (PanelMiddleware -> LicencesService) que grava o dispositivo na sessao, e e a sessao que
      // ocupa o assento de licenca.
      headers = headers.set('client-instance-id', utils.getClientInstanceId());

      // Tarefa 5 - modulo da tela que fez a requisicao, para o heartbeat gravar "quem esta em qual
      // modulo". Sai do `requiredModules` da rota ativa.
      const currentModule = this.currentModule();
      if (currentModule) {
        headers = headers.set('current-module', currentModule);
        headers = headers.set('current-module-path', this.router.url.split(/[?#]/)[0]);
      }
    }

    if (user?.id && !req.url.startsWith('https://reverse.teorema.inf.br/teoremaee')) {
      headers = headers.set('UserId', user.id);
    }

    let auth = headers.get('Authorization');
    if (auth) {
      if (auth == 'none') {
        headers = headers.delete('Authorization');
      } else if (auth.startsWith('Basic')) {
        return next.handle(req);
      }
    } else {
      if (token && token != 'UNAUTHORIZED') {
        headers = headers.set('Authorization', 'Bearer ' + token);
      }
    }

    let dupReq = req.clone({ headers });

    return new Observable(observer => {
      next.handle(dupReq).subscribe({
        next: (res: any) => {
          res.messageShown = this.showServerMessages(res?.body?._messages);
          if (res instanceof HttpResponse) {
            this.readLicenseSessionStatus(res.headers);
            observer.next(res);
          }
        },
        error: (err: any) => {
          err.messageShown = this.showServerMessages(err?.error?._messages);

          // Chamado #0000577675 - assento em uso por outro dispositivo. NAO desloga (ata de
          // 21/09): bloqueia a tela e deixa o usuario fazer o switch.
          if (err.status == 423 && err?.error?.code == 'LICENSE_SESSION_BLOCKED') {
            this.licenseSessionLock.lockWith({
              message: err?.error?.message,
              vendor_system_name: err?.error?.vendor_system_name,
              device_name: err?.error?.device_name,
            });
          } else if (err?.headers) {
            this.readLicenseSessionStatus(err.headers);
          }

          // Cadastro do cliente irregular na base da Teorema: o backend corta a
          // sessão em qualquer requisição, não só no login. Sem tratar aqui, o
          // usuário só veria erros genéricos espalhados pela tela, sem entender
          // que perdeu o acesso — então o motivo viaja até o login e a sessão é
          // encerrada.
          if (err.status == 403 && err?.error?.code == 'CADASTRO_IRREGULAR') {
            this.authService.blockedMessage = err?.error?.errors?.[0] || err?.error?.message;
            if (!this.router.url.includes('/auth')) {
              this.authService.logout();
            }
          }

          if (err.status == 401) {
            // Verifica se a requisição tem o header Skip-401-Handling
            const skipLogout = dupReq.headers.has('Skip-401-Handling');

            // Chamado #0000577675 — a sessão foi encerrada (administrativo, licença removida):
            // o recado vai até a tela de login, como no cadastro irregular.
            if (err?.error?.code == 'SESSAO_ENCERRADA') {
              this.authService.blockedMessage = err?.error?.message;
            }

            if (!skipLogout && !this.router.url.includes('/cadastrar-empresa')) {
              //this.toastService.show({ color: 'warn', message: 'Sessão expirada' })
              this.authService.logout();
            }
          }
          observer.error(err);
        },
      });
    });
  }

  /**
   * `requiredModules` da rota ativa, do nó mais profundo que declarar. Mesmo percurso do
   * `setTitleByRoute`; os filhos herdam o módulo do pai quando não declaram o próprio.
   *
   * Rota sem `requiredModules` cai no menu (`moduleFromMenu`): boa parte das rotas do V3ndor
   * (pedidos, clientes, itens, notas...) é protegida pelo `v3ndorGuard` e não declara módulo,
   * mas o item de menu dela declara.
   */
  private currentModule(): string | null {
    let route: ActivatedRouteSnapshot | null = this.router.routerState?.snapshot?.root ?? null;
    let modules: string[] | null = null;

    while (route) {
      const declared = route.data?.['requiredModules'];
      if (Array.isArray(declared) && declared.length) {
        modules = declared;
      }
      route = route.firstChild;
    }

    return modules?.join(',') || this.moduleFromMenu();
  }

  /**
   * Módulo do item de menu com o maior prefixo da URL atual, respeitando o limite de segmento:
   * `/v3ndor/pedidos/123` casa com `/v3ndor/pedidos`; `/v3ndor/compras/pedidos` não.
   */
  private moduleFromMenu(): string | null {
    const path = (this.router.url ?? '').split(/[?#]/)[0].replace(/\/+$/, '');
    if (!path) return null;

    menuModules ??= buildMenuModules(navItemsV2).sort((a, b) => b.url.length - a.url.length);

    const item = menuModules.find(i => path === i.url || path.startsWith(i.url + '/'));
    return item ? item.modules.join(',') : null;
  }

  /** Só reage quando o back manda o header; ausência não muda nada. */
  private readLicenseSessionStatus(headers?: HttpHeaders) {
    const status = headers?.get(LICENSE_SESSION_STATUS_HEADER);

    if (status === 'blocked') {
      this.licenseSessionLock.lockWith();
    } else if (status === 'active') {
      this.licenseSessionLock.unlock();
    }
  }

  private showServerMessages(messages?: any): boolean {
    if (!messages) return false;
    let messageShown = false;
    const successes = messages?.successes;
    const errors = messages?.errors;
    const infos = messages?.infos;
    successes?.forEach((message: string) => {
      this.toastService.show({ color: 'success', message });
      messageShown = true;
    });
    errors?.forEach((message: string) => {
      this.toastService.show({ color: 'error', message });
      messageShown = true;
    });
    infos?.forEach((message: string) => {
      this.toastService.show({ color: 'info', message });
      messageShown = true;
    });

    return messageShown;
  }
}
